import { PartImageItem, PartImageItemRegion } from '@/api/generatedApi';
import { useEffect, useRef, useState } from 'react';

export const MAX_CLIPS_PER_ITEM = 3;
const CLIP_MARGIN = 0.1;
const CLIP_MAX_SIDE = 480;
export const OUTLINE_COLOR = '#ffb300';
const OUTLINE_WIDTH = 10;
const OUTSIDE_DIMMING = 'rgba(0, 0, 0, 0.55)';

export interface PixelRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const clamp = (value: number, max: number): number => Math.min(Math.max(value, 0), max);

function boxRect(region: PartImageItemRegion, imageWidth: number, imageHeight: number, margin: number): PixelRect {
  const marginX = region.width * margin;
  const marginY = region.height * margin;
  const left = clamp(region.left - marginX, 1);
  const top = clamp(region.top - marginY, 1);
  const right = clamp(region.left + region.width + marginX, 1);
  const bottom = clamp(region.top + region.height + marginY, 1);
  return {
    x: Math.round(left * imageWidth),
    y: Math.round(top * imageHeight),
    width: Math.max(1, Math.round((right - left) * imageWidth)),
    height: Math.max(1, Math.round((bottom - top) * imageHeight)),
  };
}

export const paddedCropRect = (region: PartImageItemRegion, imageWidth: number, imageHeight: number): PixelRect =>
  boxRect(region, imageWidth, imageHeight, CLIP_MARGIN);

export const outlineRect = (region: PartImageItemRegion, imageWidth: number, imageHeight: number): PixelRect =>
  boxRect(region, imageWidth, imageHeight, 0);

function scaledCanvas(width: number, height: number): HTMLCanvasElement {
  const scale = Math.min(1, CLIP_MAX_SIDE / Math.max(width, height));
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(width * scale);
  canvas.height = Math.round(height * scale);
  return canvas;
}

async function canvasToUrl(canvas: HTMLCanvasElement): Promise<string> {
  const blob = await new Promise<Blob | null>(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.85));
  return blob ? URL.createObjectURL(blob) : '';
}

function cropToUrl(bitmap: ImageBitmap, region: PartImageItemRegion): Promise<string> {
  const rect = paddedCropRect(region, bitmap.width, bitmap.height);
  const canvas = scaledCanvas(rect.width, rect.height);
  canvas
    .getContext('2d')
    ?.drawImage(bitmap, rect.x, rect.y, rect.width, rect.height, 0, 0, canvas.width, canvas.height);
  return canvasToUrl(canvas);
}

function outlineToUrl(bitmap: ImageBitmap, region: PartImageItemRegion): Promise<string> {
  const canvas = scaledCanvas(bitmap.width, bitmap.height);
  const context = canvas.getContext('2d');
  if (context) {
    const rect = outlineRect(region, canvas.width, canvas.height);
    context.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    context.fillStyle = OUTSIDE_DIMMING;
    context.fillRect(0, 0, canvas.width, canvas.height);
    const source = outlineRect(region, bitmap.width, bitmap.height);
    context.drawImage(bitmap, source.x, source.y, source.width, source.height, rect.x, rect.y, rect.width, rect.height);
    context.strokeStyle = OUTLINE_COLOR;
    context.lineWidth = OUTLINE_WIDTH;
    context.strokeRect(rect.x, rect.y, rect.width, rect.height);
  }
  return canvasToUrl(canvas);
}

export interface PartImageItemClip {
  photoNumber: number;
  url: string;
  outlineUrl: string;
}

async function buildClip(bitmap: ImageBitmap, region: PartImageItemRegion): Promise<PartImageItemClip> {
  const [url, outlineUrl] = await Promise.all([cropToUrl(bitmap, region), outlineToUrl(bitmap, region)]);
  return { photoNumber: region.photoNumber, url, outlineUrl };
}

const clipRegions = (item: PartImageItem): PartImageItemRegion[] => item.regions.slice(0, MAX_CLIPS_PER_ITEM);

async function buildClips(images: File[], items: PartImageItem[]): Promise<PartImageItemClip[][]> {
  const photoNumbers = [...new Set(items.flatMap(item => clipRegions(item).map(region => region.photoNumber)))];
  const bitmaps = new Map<number, ImageBitmap>();
  await Promise.all(
    photoNumbers.map(async photoNumber => {
      const file = images[photoNumber - 1];
      if (file) bitmaps.set(photoNumber, await createImageBitmap(file, { imageOrientation: 'from-image' }));
    })
  );
  const clips = await Promise.all(
    items.map(item =>
      Promise.all(
        clipRegions(item).flatMap(region => {
          const bitmap = bitmaps.get(region.photoNumber);
          return bitmap ? [buildClip(bitmap, region)] : [];
        })
      )
    )
  );
  for (const bitmap of bitmaps.values()) bitmap.close();
  return clips.map(itemClips => itemClips.filter(clip => clip.url && clip.outlineUrl));
}

const revokeAll = (clips: PartImageItemClip[][]) => {
  for (const clip of clips.flat()) {
    URL.revokeObjectURL(clip.url);
    URL.revokeObjectURL(clip.outlineUrl);
  }
};

export function usePartImageItemClips(images: File[], items: PartImageItem[]): PartImageItemClip[][] {
  const [clips, setClips] = useState<PartImageItemClip[][]>([]);
  const shown = useRef(clips);
  shown.current = clips;
  useEffect(() => () => revokeAll(shown.current), []);
  useEffect(() => {
    let cancelled = false;
    buildClips(images, items)
      .then(result => {
        if (cancelled) revokeAll(result);
        else
          setClips(previous => {
            revokeAll(previous);
            return result;
          });
      })
      .catch(() => setClips([]));
    return () => {
      cancelled = true;
    };
  }, [images, items]);
  return clips;
}
